import {Divider, FormHelperText, ListSubheader, MenuItem, Select, Tooltip} from "@mui/material";
import * as React from "react";
import {defineMessage, defineMessages, FormattedMessage, MessageDescriptor} from "react-intl";
import {ErrorWithContext, MultipleLineError} from "@dvrt/graphql-widgets";
import {insertSeparator} from "@/tools";

import type {JSX} from "react";

export interface Endpoint {
    // short number fields
    number?: {
        shortNumber?: string;
    };
    shortNumber?: string;

    // name fields
    displayName?: string;
    name?: string;
}

export const formatEndpoint = (endpoint?: Endpoint): string | undefined => {
    if (!endpoint) return undefined;

    const name = endpoint.displayName || endpoint.name;
    const shortNumber = endpoint.number?.shortNumber || endpoint.shortNumber;

    if (!name && !shortNumber) {
        return undefined;
    }

    if (name && shortNumber) return `${name} (${shortNumber})`;
    if (name) return name;
    else return shortNumber;
};

/**
 * Types the target selector can group by.
 */
export enum TargetType {
    EXTENSION = "extensions",
    FAX = "faxes",
    GROUP = "callGroups",
    CALL_QUEUE = "callQueues",
    IVR = "ivrs",
    GW = "gateways",
    DOMAIN = "domains",
}

// Typed messages for target type group headers
const TARGET_TYPE_MESSAGES = defineMessages<TargetType>({
    extensions: {id: "extensions", defaultMessage: "Users"},
    faxes: {id: "faxes", defaultMessage: "Faxes"},
    callGroups: {id: "callGroups", defaultMessage: "Call groups"},
    callQueues: {id: "callQueues", defaultMessage: "Call queues"},
    ivrs: {id: "ivrs", defaultMessage: "Auto attendants"},
    gateways: {id: "gateways", defaultMessage: "Gateways"},
    domains: {id: "domains.label", defaultMessage: "Domains"},
});

/**
 * Base type for about-to-be-targets.
 * Endpoints queried via generic queries should fit to this
 * type out of the box.
 **/
interface TargetBase {
    number?: {
        id: number;
        shortNumber?: string;
    };
    displayName?: string;
    name?: string;
}

/**
 * Create a list of targets from a list of TargetBase objects.
 *
 * @param targets List of TargetBase objects
 * @param type Type of the targets to be created
 * @returns A list of targets
 **/
export const createTargets = (targets: TargetBase[], type: TargetType): Target[] =>
    targets.map((target) => ({
        numberId: target.number.id,
        displayName: target.displayName ?? target.name,
        shortNumber: target.number.shortNumber,
        type,
    }));

/**
 * Target interface used in `TargetSelector` to display and handle targets.
 *
 * `numberId` | id of the target.
 *
 * `displayName` | display name of the target.
 *
 * `shortNumber` | short number of the target.
 *
 * `type` | target type of the target.
 */
export interface Target {
    numberId: number; // Number id of the target
    displayName?: string; // Name that will be displayed
    shortNumber: string; // Short number of the target to be displayed
    type: TargetType; // Type of the target
}

type GroupedTargets = {
    [key in TargetType]: Target[];
};

/**
 * Sort targets by display name first, then by short number as fallback.
 * Both are treated as strings for lexicographic sorting.
 *
 * @param targets Array of targets to sort
 * @returns Sorted array of targets
 */
function sortTargets(targets: Target[]): Target[] {
    return targets.sort((a, b) => {
        // Sort by displayName first, then by shortNumber as fallback
        const nameA = a.displayName || a.shortNumber || "";
        const nameB = b.displayName || b.shortNumber || "";
        return nameA.localeCompare(nameB, undefined, {
            sensitivity: "base",
        });
    });
}

const defaultNotSelectedText = defineMessage({id: "notSelected", defaultMessage: "Not selected"});

type Props = {
    selected: number | "" | undefined;
    handleChange: (newTargetNumberId: number) => void;
    targets: Target[];
    allowWithoutShortNumber?: boolean;
    notSelectedText?: MessageDescriptor;
    variant?: "outlined" | "filled" | "standard";
    errors?: ErrorWithContext[];
    style?: React.CSSProperties;
    disabled?: boolean;
} & (
    | {
          required: true;
          autoSelectFirst?: boolean;
      }
    | {
          required?: boolean;
          autoSelectFirst?: never;
      }
);

function getGroupedTargets(targets: Target[], allowWithoutShortNumber: boolean): GroupedTargets {
    const result: GroupedTargets = {
        [TargetType.EXTENSION]: [],
        [TargetType.FAX]: [],
        [TargetType.GROUP]: [],
        [TargetType.CALL_QUEUE]: [],
        [TargetType.IVR]: [],
        [TargetType.GW]: [],
        [TargetType.DOMAIN]: [],
    };

    targets
        .filter((target) => allowWithoutShortNumber || target.shortNumber) // can not forward call if there is no short number
        .forEach((target) => {
            result[target.type].push(target);
        });

    return result;
}

/**
 * This component is used to select a target from a list of targets.
 *
 * The number id of the selected target is passed to the handleChange callback.
 *
 * The targets are passed as a list of Target objects. The TargetType enum is used to specify the type of the target.
 * The filtering is done by this component, so the list of targets should contain all the targets that should be displayed.
 *
 * @param selected id of the selected target
 * @param handleChange callback to handle the change of the target
 * @param targets list of targets to be displayed (and allowed to be selected)
 * @param required whether the target is required or not
 * @param autoSelectFirst whether the first target should be selected automatically (defaults to false)
 * @param notSelectedText translation id for the text to display when no target is selected (and is not required)
 * @param variant material ui variant
 * @param errors errors to display for this target
 * @param style style of the component
 * @param disabled whether the component is disabled or not
 */
const TargetSelector = ({
    selected,
    handleChange,
    targets,
    required,
    allowWithoutShortNumber = false,
    autoSelectFirst = false,
    notSelectedText,
    variant,
    errors,
    style,
    disabled,
}: Props) => {
    const groupedTargets = getGroupedTargets(targets, allowWithoutShortNumber);

    // Only show subheaders if there are more than 1 type of targets
    const subheaderVisible = [...new Set(targets.map((target) => target.type))].length > 1;

    /**
     * Create a section of MenuItem components from a list of targets.
     *
     * @param targets List of targets
     * @returns A list of menu items for the targets
     */
    function createSections(targets: Target[]): JSX.Element[] | null {
        if (targets.length === 0) {
            return null;
        }

        const items: JSX.Element[] = [];

        if (subheaderVisible) {
            // Add a subheader if there are more than 1 type of targets
            items.push(
                <ListSubheader key="subheader" style={ListSubheaderStyle}>
                    <FormattedMessage {...TARGET_TYPE_MESSAGES[targets[0].type]} />
                </ListSubheader>,
            );
        }

        // Returning a list of menu items for further operations
        return items.concat(
            targets.map((target, index) => (
                <MenuItem value={target.numberId} key={index}>
                    {formatEndpoint(target) ?? <InvalidItemName />}
                </MenuItem>
            )),
        );
    }

    /**
     * Create the menu items for the select component.
     *
     * @returns A list of menu items for the select component,
     * grouped by their type and separated by dividers
     */
    function createMenuItems(): JSX.Element[][] {
        const notSelected = required ? null : (
            <MenuItem key={0} value={0}>
                <FormattedMessage {...(notSelectedText ?? defaultNotSelectedText)} />
            </MenuItem>
        );

        const menuItems = [[notSelected]].concat(
            Object.values(groupedTargets).map((targets) => createSections(sortTargets(targets))),
        );

        // it's okay to let the key be 0, because it is the only element in that specific array
        return insertSeparator(
            menuItems.filter((section) => section !== null),
            [<Divider key={0} style={DividerStyle} />],
        );
    }

    if (autoSelectFirst && targets.length > 0 && selected === undefined) {
        handleChange(targets[0].numberId);
    }

    return (
        <div style={{alignItems: "center", ...style}}>
            <Select
                variant={variant ?? "outlined"}
                margin="dense"
                size="small"
                disabled={disabled}
                displayEmpty
                renderValue={(value) => {
                    const target = targets.find((target) => target.numberId === value);
                    return (
                        formatEndpoint(target) ?? (
                            <span style={required ? {fontStyle: "italic", color: "red"} : {fontStyle: "italic"}}>
                                <FormattedMessage {...(notSelectedText ?? defaultNotSelectedText)} />
                            </span>
                        )
                    );
                }}
                value={selected ?? ""}
                onChange={(event) => {
                    handleChange(event.target.value);
                }}
                className="d-none"
                error={errors?.length > 0}
            >
                {...createMenuItems()}
            </Select>
            {errors?.length > 0 && (
                <FormHelperText style={{color: "red"}}>
                    <MultipleLineError errors={errors} />
                </FormHelperText>
            )}
        </div>
    );
};

/**
 * If the name can not be displayed, this component is displayed instead.
 *
 * @returns Formatted message with tooltip
 */
const InvalidItemName = () => {
    return (
        <Tooltip
            title={
                <FormattedMessage
                    id="target_selector.can_not_display"
                    defaultMessage="Failed to display name and short number"
                />
            }
        >
            <div style={{color: "red"}}>
                <FormattedMessage id="target_selector.unknown" defaultMessage="Unknown" />
            </div>
        </Tooltip>
    );
};

const ListSubheaderStyle = {
    cursor: "default", // Prevent the cursor from changing to 'select text' when hovering over the subheader
    userSelect: "none", // Prevent user from selecting text in the subheader
    pointerEvents: "none", // Prevent user from clicking on the subheader (which is a bad behavior)
} as React.CSSProperties;

// To make it a bit more aesthetic
const DividerStyle = {
    marginTop: "0.5rem",
    marginBottom: "0.5rem",
    marginLeft: "auto",
    marginRight: "auto",
    width: "90%",
} as React.CSSProperties;

export default TargetSelector;
